import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import SashaAvatar from "../components/SashaAvatar";
import ConsentBlock from "../components/ConsentBlock";

export default function RegisterPage() {
  const { register } = useAuth();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "host" ? "host" : "client";
  // Pre-fill the referral code from the URL (?ref=JT-REF-XXXXXX). Persisted
  // in sessionStorage so it survives a navigation if the visitor clicks
  // around before submitting.
  const refFromUrl = (params.get("ref") || "").trim().toUpperCase();
  React.useEffect(() => {
    if (refFromUrl) {
      try {
        window.sessionStorage.setItem("jante_referral_code", refFromUrl);
      } catch {
        /* storage blocked */
      }
    }
  }, [refFromUrl]);
  const storedRef = (() => {
    try {
      return window.sessionStorage.getItem("jante_referral_code") || "";
    } catch {
      return "";
    }
  })();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: initialRole,
    referral_code: refFromUrl || storedRef,
  });
  const [consents, setConsents] = useState({ terms: false, sms: false, calls: false, marketing: false });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const next = params.get("next") || (initialRole === "host" ? "/host" : "/portal");

  const submit = async (e) => {
    e.preventDefault();
    if (!consents.terms) {
      toast.error("Please confirm you have read and agree to our policies.");
      return;
    }
    setBusy(true);
    try {
      await register({
        ...form,
        referral_code: form.referral_code ? form.referral_code.trim().toUpperCase() : null,
        consents,
      });
      try {
        window.sessionStorage.removeItem("jante_referral_code");
      } catch {
        /* noop */
      }
      toast.success("Welcome to Janté.");
      navigate(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-2 gap-16 items-center" data-testid="register-page">
      <div className="hidden lg:block">
        <SashaAvatar size="lg" />
      </div>
      <div>
        <div className="overline">Join Club Janté</div>
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">
          {form.role === "host" ? "List your accommodations." : "Begin your journey."}
        </h1>
        <p className="text-[var(--ink-2)] mt-3">A few details and Sasha is ready.</p>

        <div className="mt-8 inline-flex p-1 border border-[var(--emerald)]/15">
          <button
            type="button"
            onClick={() => setForm({ ...form, role: "client" })}
            className={`px-5 py-2 text-xs uppercase tracking-[0.2em] transition ${
              form.role === "client" ? "bg-[var(--emerald)] text-[var(--ivory)]" : "text-[var(--emerald)]"
            }`}
            data-testid="role-client"
          >
            Guest
          </button>
          <button
            type="button"
            onClick={() => setForm({ ...form, role: "host" })}
            className={`px-5 py-2 text-xs uppercase tracking-[0.2em] transition ${
              form.role === "host" ? "bg-[var(--emerald)] text-[var(--ivory)]" : "text-[var(--emerald)]"
            }`}
            data-testid="role-host"
          >
            Host
          </button>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <div>
            <div className="overline mb-1">Name</div>
            <input className="lux-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
          </div>
          <div>
            <div className="overline mb-1">Email</div>
            <input className="lux-input" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
          </div>
          <div>
            <div className="overline mb-1">Password</div>
            <input className="lux-input" type="password" required minLength={6} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
          </div>
          <div>
            <div className="overline mb-1">
              Referral code <span className="text-[var(--muted)] normal-case tracking-normal">· optional</span>
            </div>
            <input
              className="lux-input uppercase tracking-[0.15em]"
              type="text"
              placeholder="JT-REF-XXXXXX"
              value={form.referral_code}
              onChange={(e) =>
                setForm({ ...form, referral_code: e.target.value.toUpperCase() })
              }
              data-testid="register-referral-code"
            />
            {form.referral_code && (
              <p className="text-[11px] text-[var(--gold)] mt-1.5">
                Got it — your friend earns $100 when you book your first trip.
              </p>
            )}
          </div>
          <ConsentBlock value={consents} onChange={setConsents} testIdPrefix="register-consent" />
          <button type="submit" disabled={busy || !consents.terms} className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed" data-testid="register-submit">
            {busy ? "Creating account…" : "Create account"}
          </button>
          <p className="text-sm text-[var(--ink-2)]">
            Already a member? <Link to="/login" className="text-[var(--gold)] hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
